/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: Presentational
 * Reason: Horizontal stepper visualising the 7 pipeline phases of an EI import run.
 *         Uses the import's current phase and execution status to colorize circles
 *         and connector lines. Shown in the tooling area above the tabs.
 */
import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {EiImportDto} from '../../../../../../../model/dto/entity/ei-import.dto';
import {EiImportRecordDto} from '../../../../../../../model/dto/entity/ei-import-record.dto';
import {EiImportPhase} from '../../../../../../../enum/ei-import-phase.enum';
import {EiImportExecutionStatus} from '../../../../../../../enum/ei-import-execution-status.enum';

interface PhaseDescriptor {
    key: EiImportPhase;
    label: string;
    icon: string;
}

@Component({
    selector: 'ei-tooling-stage-visualizer-compact',
    templateUrl: './ei-tooling-stage-visualizer-compact.component.html',
    styleUrls: ['./ei-tooling-stage-visualizer-compact.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingStageVisualizerCompactComponent {

    @Input() parentImport: EiImportDto | null = null;
    @Input() record: EiImportRecordDto | null = null;

    /** Ordered list of all 7 pipeline phases. */
    readonly allPhases: PhaseDescriptor[] = [
        {key: EiImportPhase.INGESTION, label: 'Ingestion', icon: 'fa-regular fa-inbox'},
        {key: EiImportPhase.OBJECT_RESOLUTION, label: 'Objekt-Auflösung', icon: 'fa-regular fa-magnifying-glass'},
        {key: EiImportPhase.OBJECT_REVIEW, label: 'Objekt-Prüfung', icon: 'fa-regular fa-user-check'},
        {key: EiImportPhase.VALIDATION, label: 'Validierung', icon: 'fa-regular fa-circle-check'},
        {key: EiImportPhase.PERSISTENCE, label: 'Persistenz', icon: 'fa-regular fa-database'},
        {key: EiImportPhase.POST_PROCESSING, label: 'Nachbearbeitung', icon: 'fa-regular fa-gears'},
        {key: EiImportPhase.DONE, label: 'Abgeschlossen', icon: 'fa-regular fa-flag-checkered'},
    ];

    /**
     * Tooltip for each phase (hovered in the compact visualizer).
     */
    phaseTooltip(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        return phase.label + ' – ' + this.statusLabel(status);
    }

    /**
     * Returns the CSS class for the phase circle (size + shape).
     */
    phaseCircleClass(_phase: PhaseDescriptor): string {
        return 'ei-phase-circle border-circle flex align-items-center justify-content-center text-white';
    }

    /**
     * Returns a color value for a phase circle based on its status.
     */
    phaseCircleBackground(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        switch (status) {
            case 'done': return 'var(--green-500)';
            case 'current-running': return 'var(--orange-500)';
            case 'current-waiting': return 'var(--yellow-500)';
            case 'current-error': return 'var(--red-500)';
            case 'current-idle': return 'var(--blue-500)';
            case 'pending': return 'var(--surface-300)';
            default: return 'var(--surface-300)';
        }
    }

    /**
     * Returns the connector line color (between this phase and the next).
     */
    connectorColor(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        if (status === 'done') {
            return 'var(--green-500)';
        }
        return 'var(--surface-300)';
    }

    /**
     * Computes the status of a phase relative to the import's current phase.
     */
    getPhaseStatus(phaseKey: EiImportPhase):
        'done' | 'current-running' | 'current-waiting' | 'current-error' | 'current-idle' | 'pending' {
        const current = this.parentImport?.phase;
        if (current == null) {
            return 'pending';
        }
        if (phaseKey < current) {
            return 'done';
        }
        if (phaseKey > current) {
            return 'pending';
        }
        // phaseKey === current
        const exec = this.parentImport?.executionStatus;
        if (exec === EiImportExecutionStatus.ERROR) {
            return 'current-error';
        }
        if (exec === EiImportExecutionStatus.RUNNING) {
            return 'current-running';
        }
        if (phaseKey === EiImportPhase.OBJECT_REVIEW) {
            return 'current-waiting';
        }
        if (phaseKey === EiImportPhase.DONE) {
            return 'done';
        }
        return 'current-idle';
    }

    /**
     * Text label for a phase status (used for tooltips).
     */
    private statusLabel(status: string): string {
        switch (status) {
            case 'done': return 'abgeschlossen';
            case 'current-running': return 'läuft';
            case 'current-waiting': return 'wartet auf Aktion';
            case 'current-error': return 'Fehler';
            case 'current-idle': return 'aktuell';
            case 'pending': return 'offen';
            default: return '';
        }
    }
}
